import { useState, FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../App'
import { api } from '../services/api'
import { generateRegistrationKeys } from '../hooks/useSignalProtocol'

export default function RegisterPage() {
  const [username, setUsername] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const navigate = useNavigate()
  const { login } = useAuth()

  const validateUsername = (value: string): string | null => {
    if (!value.trim()) {
      return 'Please enter a username'
    }
    if (value.length < 3) {
      return 'Username must be at least 3 characters'
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return 'Only letters, numbers, and underscores allowed'
    }
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const validationError = validateUsername(username)
    if (validationError) {
      setError(validationError)
      return
    }

    if (!agreedToTerms) {
      setError('Please agree to the privacy policy')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Generate Signal Protocol keys
      const keys = generateRegistrationKeys()

      // Register with backend
      const response = await api.register(username.trim(), keys)
      // Use user ID from backend response (UUID), not username
      const userId = response.user?.id || username.trim()
      login(userId, response.user?.username || username.trim(), response.token)

      // Store UUID for E2EE key derivation if backend returns UUID
      if (response.user?.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(response.user.id)) {
        localStorage.setItem('lynkstar_my_uuid', response.user.id)
        console.log('[Register] Stored UUID for E2EE:', response.user.id)
      }

      navigate('/chats')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Create Account
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          Your messages will be end-to-end encrypted. Only you and the recipient can read them.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username Input */}
          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="input-field pl-12"
                disabled={isLoading}
                autoComplete="username"
                autoFocus
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              This will be your unique identifier
            </p>
          </div>

          {/* Key Generation Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              <span className="font-semibold text-blue-700 dark:text-blue-300">
                Key Generation
              </span>
            </div>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              We will generate secure encryption keys on your device:
            </p>
            <ul className="text-sm text-blue-600 dark:text-blue-400 mt-2 ml-4 list-disc">
              <li>Identity Key - Your unique cryptographic identity</li>
              <li>Pre-Keys - For establishing encrypted sessions</li>
            </ul>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 font-medium">
              These keys never leave your device.
            </p>
          </div>

          {/* Privacy Policy Agreement */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="terms" className="text-sm text-gray-600 dark:text-gray-400">
              I agree to the{' '}
              <a href="#" className="text-primary font-medium hover:underline">
                Privacy Policy
              </a>{' '}
              and understand that my messages are end-to-end encrypted
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 text-error text-sm px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          {/* Register Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="spinner mr-2" />
                Generating Keys...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Security Badge */}
        <div className="mt-6 flex items-center justify-center gap-2 text-green-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <span className="text-sm">Protected by Signal Protocol</span>
        </div>

        {/* Login Link */}
        <div className="mt-8 text-center">
          <span className="text-gray-500 dark:text-gray-400">
            Already have an account?{' '}
          </span>
          <Link
            to="/login"
            className="text-primary font-medium hover:underline"
          >
            Sign In
          </Link>
        </div>

        {/* Brand */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Powered by <span className="font-semibold text-primary">LYNKSTAR</span>
          </p>
        </div>
      </div>
    </div>
  )
}
